import { DEFAULT_GATEWAYS, Protocol, parse } from "bitcoin-image";
import {
	contentUrlForOutpoint,
	extractInscriptionOutpoint,
	type OrdImageFit,
	type OrdImageOptions,
	ordfsImageUrl,
	resolveOrdContentUrl,
} from "@/lib/ord-content";

export type { OrdImageOptions } from "@/lib/ord-content";

export const DISPLAY_IMAGE = {
	avatar: { w: 80, h: 80, fit: "fill", q: 85 },
	avatarMenu: { w: 64, h: 64, fit: "fill", q: 85 },
	avatarHero: { w: 160, h: 160, fit: "fill", q: 90 },
	profile: { w: 400, h: 400, fit: "fill", q: 85 },
	banner: { w: 1500, h: 500, fit: "fill", q: 85 },
	bannerEmail: { w: 1200, h: 400, fit: "fill", q: 85 },
	editorBanner: { w: 1800, h: 900, fit: "fit", q: 90 },
	editorProfile: { w: 1200, h: 1200, fit: "fit", q: 90 },
	thumb: { w: 200, h: 200, fit: "fill", q: 75 },
	ordinalBanner: { w: 600, h: 338, fit: "fill", q: 80, f: "webp" },
	ordinalProfile: { w: 300, h: 300, fit: "fill", q: 80, f: "webp" },
} as const satisfies Record<string, OrdImageOptions>;

const DEFAULT_DISPLAY: OrdImageOptions = {
	w: 256,
	h: 256,
	fit: "fill",
	q: 80,
};

function cropForFit(fit: OrdImageFit): string {
	if (fit === "fill") {
		return "fill";
	}
	if (fit === "fit") {
		return "fit";
	}
	if (fit === "limit") {
		return "inside";
	}
	throw new Error(`Unsupported Sharp crop for fit "${fit}"`);
}

export function toProxyTransformPath(options: OrdImageOptions): string {
	const parts: string[] = [];
	if (options.w !== undefined) {
		parts.push(`w_${options.w}`);
	}
	if (options.h !== undefined) {
		parts.push(`h_${options.h}`);
	}
	if (options.fit !== undefined) {
		parts.push(`c_${cropForFit(options.fit)}`);
	}
	if (options.q !== undefined) {
		parts.push(`q_${options.q}`);
	}
	if (options.f !== undefined) {
		parts.push(`f_${options.f}`);
	}
	return parts.join(",");
}

/**
 * Normalize image URLs to the 1sat content gateway using bitcoin-image.
 * Handles b://, ord://, bitfs://, relative paths, and txid references.
 */
export function normalizeImageUrl(url: string | undefined | null): string {
	if (!url || typeof url !== "string" || url.trim() === "") {
		return "";
	}

	const trimmed = url.trim();

	// Parse the URL using bitcoin-image
	const parsed = parse(trimmed, { allowNativeTxid: true });

	if (!parsed.isValid) {
		return trimmed;
	}

	// Build display URL based on protocol
	switch (parsed.protocol) {
		case Protocol.DataURI:
			return parsed.original;

		case Protocol.HTTP: {
			try {
				return resolveOrdContentUrl(new URL(parsed.original)).href;
			} catch {
				return parsed.original;
			}
		}

		case Protocol.Bitcoin:
		case Protocol.Ordinals:
		case Protocol.Native: {
			if (!parsed.txid) {
				return trimmed;
			}
			const vout = parsed.vout ?? 0;
			return contentUrlForOutpoint(`${parsed.txid}_${vout}`);
		}

		case Protocol.BitFS: {
			if (!parsed.txid) {
				return trimmed;
			}
			if (parsed.vout !== undefined) {
				return contentUrlForOutpoint(`${parsed.txid}_${parsed.vout}`);
			}
			return `${DEFAULT_GATEWAYS.bitfs}/${parsed.txid}`;
		}

		case Protocol.IPFS: {
			if (!parsed.hash) {
				return trimmed;
			}
			return `${DEFAULT_GATEWAYS.ipfs}/${parsed.hash}`;
		}

		default:
			return trimmed;
	}
}

/**
 * Build a display URL. Inscriptions go to the 1sat transform API.
 * External rasters stay on the local Sharp proxy.
 */
export function getDisplayImageUrl(
	imageData: string | undefined | null,
	fallbackUrl: string | null = null,
	options: OrdImageOptions = DEFAULT_DISPLAY
): string | null {
	if (!imageData) {
		return fallbackUrl;
	}

	const trimmed = imageData.trim();

	// Parse using bitcoin-image
	const parsed = parse(trimmed, { allowNativeTxid: true });

	// Data URIs - return as-is (no proxying needed)
	if (parsed.protocol === Protocol.DataURI) {
		return parsed.original;
	}

	const outpoint =
		parsed.txid &&
		(parsed.protocol === Protocol.Bitcoin ||
			parsed.protocol === Protocol.Ordinals ||
			parsed.protocol === Protocol.Native ||
			parsed.protocol === Protocol.BitFS)
			? `${parsed.txid}_${parsed.vout ?? 0}`
			: extractInscriptionOutpoint(trimmed);
	if (outpoint) {
		return ordfsImageUrl(outpoint, options);
	}

	// Get normalized URL
	const normalizedUrl = normalizeImageUrl(trimmed);

	// Local paths (not API routes) - return as-is
	if (normalizedUrl.startsWith("/") && !normalizedUrl.startsWith("/api/")) {
		return normalizedUrl;
	}

	// External http/https URLs - proxy through image API for optimization
	if (normalizedUrl.startsWith("http")) {
		const encodedUrl = encodeURIComponent(normalizedUrl);
		return `/api/image/fetch/${toProxyTransformPath(options)}/${encodedUrl}`;
	}

	// Unrecognized format - return fallback
	return fallbackUrl;
}
